import Link from "next/link";
import { useSelector } from "react-redux";
import { useRouter } from "next/router";
import Image from "next/image";

import {
  Button,
  Card,
  FloatingActionBar,
  StandaloneCardSimple,
} from "@chef/components";
import {
  ACCOUNT_STATUSES,
  BASE_64_1X1_GREY,
  BRAND_NAME,
  BRAND_PATHS,
  config,
  CONSENT_IDS,
  PRODUCT_TYPE_IDS,
} from "@chef/constants";
import {
  IDeviationProduct,
  selectDeviationBasket,
  showNotification,
  useBillingQuery,
  useCreateOrUpdateDeviationByBasketIdMutation,
  useCreateOrUpdateMultipleSchedulersMutation,
  usePickAndMixQuery,
  useProductCategoriesQuery,
  useProductsByCategoriesQuery,
  useUpdateBillingAgreementMutation,
  useValidateBasketMutation,
  useCreateOrUpdateSchedulerMutation,
} from "@chef/state-management";
import { isEqualStrings } from "@chef/utils/equal";
import {
  ErrorComponent,
  CurrencyWrapper as Currency,
  PageLoader,
  ShoppingCart,
} from "@chef/smart-components";
import {
  useCalendarData,
  useExtendedBasketData,
  usePreselector,
  useTrack,
} from "@chef/state-management/hooks";
import {
  createReactivationSchedulers,
  isAccountPaused,
  isAddonProduct,
  isGroceryBasketType,
  isMealboxBasketType,
  isPickAndMixProduct,
  isSingleProduct,
  isStandaloneProduct,
} from "@chef/helpers";
import {
  compareBasketsProducts,
  getBasketFinances,
  getCurrentWeekDataAndBasketFromCalendar,
  getImageFromVariation,
  getStandaloneProductCategoryIdsFromProductCategories,
  getVariationFromProductsByCategories,
  getWeeklyTimeblockDataFromCalendar,
} from "@chef/state-management/helpers";
import { useDebounce, useEffectOnce, useIntercom } from "@chef/hooks";
import { AppShell } from "@chef/feature-app-shell";
import { useLoyalty } from "@chef/state-management/hooks";
import { isEmptyArray, sum } from "@chef/utils/array";

import { useCreateDeviationBasket } from "../hooks";
import { filterBasketItems, getSteps } from "../utils";
import { PickAndMixCardGrid } from "../lib/PickAndMixCardGrid";

import { intl } from "./WeekEditorConfirmOrderDefault.Intl";

interface WeekEditorConfirmOrderDefaultProps {
  week: number;
  year: number;
}

export const WeekEditorConfirmOrderDefault = ({
  week,
  year,
}: WeekEditorConfirmOrderDefaultProps) => {
  const router = useRouter();
  const track = useTrack();

  useIntercom(false);

  const { trigger } = usePreselector();

  const deviationBasketForCurrentWeekYear = useSelector(
    selectDeviationBasket({ week, year }),
  );

  const deviationProductsForCurrentWeekYear =
    deviationBasketForCurrentWeekYear?.products;

  const [
    createOrUpdateDeviationByBasketId,
    { isLoading: isLoadingCreateOrUpdateDeviation },
  ] = useCreateOrUpdateDeviationByBasketIdMutation();

  const [
    createOrUpdateMultipleSchedulersMutation,
    { isLoading: isLoadingCreateOrUpdateMultipleSchedulers },
  ] = useCreateOrUpdateMultipleSchedulersMutation();

  const [validateBasketMutation, { isLoading: isLoadingValidateBasket }] =
    useValidateBasketMutation();

  const [
    updateBillingAgreementMutation,
    { isLoading: isLoadingUpdateBillingAgreementMutation },
  ] = useUpdateBillingAgreementMutation();

  const [createOrUpdateScheduler] = useCreateOrUpdateSchedulerMutation();

  const isConfirmOrderLoading =
    isLoadingUpdateBillingAgreementMutation ||
    isLoadingCreateOrUpdateDeviation ||
    isLoadingCreateOrUpdateMultipleSchedulers ||
    isLoadingValidateBasket;

  const isConfirmLoading =
    useDebounce(isConfirmOrderLoading, 50) || isConfirmOrderLoading;

  const {
    data: extendedBasketData,
    isLoading: isLoadingExtendedBasketData,
    isError: isErrorExtendedBasketData,
  } = useExtendedBasketData({ week, year });

  // queries
  const {
    data: billingData,
    isLoading: isLoadingBilling,
    isError: isErrorBilling,
  } = useBillingQuery();

  const { data: loyaltyData } = useLoyalty();

  const {
    basket,
    calendar,
    isError: isErrorCalendar,
    isLoading: isLoadingCalendar,
  } = useCalendarData({
    week,
    year,
  });

  const { isLoading: isLoadingCreateDeviationBasket } =
    useCreateDeviationBasket({ week, year });

  const {
    data: { pickAndMix = [] } = {},
    isError: isErrorPickAndMix,
    isLoading: isLoadingPickAndMix,
  } = usePickAndMixQuery({
    productTypeId: PRODUCT_TYPE_IDS.PICKANDMIX,
    week,
    year,
  });

  const {
    data: productCategoriesData,
    isError: isErrorProductCategories,
    isLoading: isLoadingProductCategories,
  } = useProductCategoriesQuery();

  const {
    data: { productsByCategories = [] } = {},
    isLoading: isLoadingProductsByCategories,
  } = useProductsByCategoriesQuery(
    {
      categoryIds: getStandaloneProductCategoryIdsFromProductCategories(
        productCategoriesData?.productCategories || [],
      ),
      week,
      year,
    },
    { skip: !productCategoriesData?.productCategories },
  );

  // misc
  const steps = getSteps();

  const isError =
    isErrorPickAndMix ||
    isErrorProductCategories ||
    isErrorExtendedBasketData ||
    isErrorCalendar ||
    isErrorBilling;

  const isLoading =
    router.isFallback ||
    isLoadingExtendedBasketData ||
    isLoadingProductsByCategories ||
    isLoadingProductCategories ||
    isLoadingCreateDeviationBasket ||
    isLoadingPickAndMix ||
    isLoadingCalendar ||
    isLoadingBilling;

  useEffectOnce(() => {
    track("deviationUpdateStepViewed", {
      affiliation: "Frontend process",
      delivery_update_step: 3,
      delivery_update_step_name: "userConfirmation",
      delivery_update_step_title: intl.HEADER,
    });
  }, !isLoading && !isError);

  if (isError) {
    return <ErrorComponent />;
  }

  if (
    isLoading ||
    !deviationProductsForCurrentWeekYear ||
    !calendar ||
    !billingData
  ) {
    return <PageLoader />;
  }

  const currentCalendarWeek = calendar.filter(
    (c) => c.week === week && c.year === year,
  );

  const selectedStandaloneWithProductsByCategory =
    deviationProductsForCurrentWeekYear
      .filter((p) => isStandaloneProduct(p) || isAddonProduct(p))
      .map((bp) => ({
        ...bp,
        variation: getVariationFromProductsByCategories({
          productsByCategories,
          variationId: bp.variationId,
          productId: bp.productId,
        }),
      }));

  const timeblock = getWeeklyTimeblockDataFromCalendar({
    calendar: currentCalendarWeek,
    week,
    year,
  });

  const finances = getBasketFinances({
    basketProducts: deviationProductsForCurrentWeekYear,
    customerFee: timeblock?.customerFee,
    loyaltyLevel: loyaltyData?.currentLevel,
  });

  const totalSum = finances.total;

  const upcomingDeviationMeals = calendar?.map((c) =>
    c.baskets
      .find((b) => b.isDefault)
      ?.basketDetails.products.filter((p) =>
        isEqualStrings(
          p.variation.product.productTypeId,
          PRODUCT_TYPE_IDS.PICKANDMIX,
        ),
      ),
  );
  const isSomeUpcomingDeviationEmpty = upcomingDeviationMeals?.some(
    (udm) => udm && isEmptyArray(udm),
  );

  const onSubmitWeek = async (selectedProducts: IDeviationProduct[]) => {
    if (!selectedProducts || !pickAndMix || !billingData.billing) {
      throw new Error("Data not initialized");
    }

    const isBeforeStartDateWeek = getCurrentWeekDataAndBasketFromCalendar({
      calendar: currentCalendarWeek,
      week,
      year,
    })?.isBeforeStartDateWeek;

    if (isBeforeStartDateWeek) {
      showNotification({
        type: "error",
        message: intl.NO_DELIVERIES_BEFORE_START_WEEK,
      });
      router.push(BRAND_PATHS.MY_MENU_PAGE);
      return;
    }

    await validateBasketMutation(
      selectedProducts.map((bp) => ({
        quantity: bp.quantity,
        variationId: bp.variationId,
      })),
    ).unwrap();

    const isPausedAccount = isAccountPaused(billingData.billing.status || 10);

    if (isPausedAccount) {
      await updateBillingAgreementMutation({
        status: ACCOUNT_STATUSES.ACTIVE,
        _suppressNotification: true,
      }).unwrap();

      await createOrUpdateMultipleSchedulersMutation({
        schedulers: createReactivationSchedulers(week, year),
        _suppressNotifications: true,
      }).unwrap();
    }

    const { mealboxBasketItems, groceryBasketItems } =
      filterBasketItems(selectedProducts);

    const groceryBasketId =
      billingData.billing.baskets.find(isGroceryBasketType)?.basketId;
    const currentWeekMealboxBasket =
      currentCalendarWeek[0]?.baskets.find(isMealboxBasketType);
    const weekIsActive = currentWeekMealboxBasket?.shippingDetails.hasDelivery;

    const mealboxBasketId =
      billingData.billing.baskets.find(isMealboxBasketType)?.basketId;

    const isMealboxBasketChanged = !compareBasketsProducts(
      basket.mealboxBasket,
      mealboxBasketItems,
    );

    const isGroceryBasketChanged = !compareBasketsProducts(
      basket.groceryBasket,
      groceryBasketItems,
    );

    if (mealboxBasketId && isMealboxBasketChanged) {
      await createOrUpdateDeviationByBasketId({
        basketId: mealboxBasketId,
        week,
        year,
        basketDeviationProducts: mealboxBasketItems.map((bp) => ({
          variationId: bp.variationId,
          quantity: bp.quantity,
        })),
      }).unwrap();
    }

    if (groceryBasketId && isGroceryBasketChanged) {
      await createOrUpdateDeviationByBasketId({
        basketId: groceryBasketId,
        week,
        year,
        basketDeviationProducts: groceryBasketItems.map((bp) => ({
          variationId: bp.variationId,
          quantity: bp.quantity,
        })),
      }).unwrap();
    }

    track("deviationUpdateStepCompleted", {
      affiliation: "Frontend process",
      delivery_update_step: 3,
      delivery_update_step_name: "userConfirmation",
      delivery_update_step_title: intl.HEADER,
    });

    track("deviationCreated", {
      affiliation: "Frontend process",
      source: "week_editor",
      is_active: true,
      products: selectedProducts.map((bp) => ({
        product_id: bp.productId,
        variant_id: bp.variationId,
        quantity: bp.quantity,
        price: bp.price,
        category_id: bp.productTypeId,
      })),
      quantity: sum(selectedProducts.map((bp) => bp.quantity)),
      total: finances.total,
      status: billingData.billing.status,
      week,
      year,
    });

    if (!isMealboxBasketChanged && !weekIsActive) {
      await createOrUpdateScheduler({ week, year, delivery: true }).unwrap();
    }
    if (isPausedAccount) {
      const hasAllergenPreferences = billingData?.billing.preferences.some(
        (p) => p.isAllergen,
      );

      const requiredConsentIds: string[] = [
        CONSENT_IDS.DATA_PROCESSING[BRAND_NAME],
      ];

      if (hasAllergenPreferences) {
        requiredConsentIds.push(CONSENT_IDS.ALLERGEN_STORING[BRAND_NAME]);
      }
      showNotification({
        type: "success",
        message: intl.NOTIFICATION_DELIVERY_STARTED,
      });
      if (isSomeUpcomingDeviationEmpty) {
        trigger({ week, year, requiredConsentIds, overrideDeviations: false });
      }
      router.push(BRAND_PATHS.RESTART_BILLING({ week, year }));
    } else {
      router.push(BRAND_PATHS.WEEK_EDITOR_CONFIRMATION_PAGE({ week, year }));
    }
  };

  const hasSingleProduct =
    deviationProductsForCurrentWeekYear.some(isSingleProduct);

  const isBelowMinMeals =
    deviationProductsForCurrentWeekYear.filter(isPickAndMixProduct).length <
    config.order.minMeals;

  return (
    <AppShell title={intl.HEADER}>
      <AppShell.Meta />

      <AppShell.Drawer />

      <AppShell.Sticky anchor="top">
        <AppShell.Header
          variant="steps"
          steps={steps}
          current={3}
          backHref={BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year })}
          exitLabel={intl.BACK}
        />
      </AppShell.Sticky>

      <AppShell.Main grid>
        <h1 className="hidden lg:block col-span-full">
          <strong>{intl.HEADER}</strong>
        </h1>

        {isAccountPaused(billingData.billing.status) && (
          <section className="p-4 col-span-full bg-informationBG">
            {intl.RESTART_DELIVERY_INFO_TEXT}
          </section>
        )}

        <div className="row-start-2 mt-4 col-span-full lg:col-span-5 xl:col-span-9 md:mt-0">
          <div className="flex justify-between">
            <div className="text-lg">
              <strong>{intl.CHOSEN_DISHES}</strong>
            </div>
            <Link
              href={BRAND_PATHS.WEEK_EDITOR_PAGE({ week, year })}
              passHref
              className="underline text-primary"
            >
              {intl.CHANGE_DISHES_LINK_TEXT}
            </Link>
          </div>
        </div>
        <div className="row-start-3 mt-4 col-span-full lg:col-span-5 xl:col-span-9 md:mt-0">
          <PickAndMixCardGrid
            items={pickAndMix.filter((pnm) =>
              deviationProductsForCurrentWeekYear.some((p) =>
                isEqualStrings(p.productId, pnm.productId),
              ),
            )}
            portions={extendedBasketData.portions}
            week={week}
            year={year}
            hideFooter
          />
          {selectedStandaloneWithProductsByCategory.length !== 0 && (
            <>
              <div className="flex justify-between row-start-4 mt-12 lg:row-start-4">
                <div className="text-lg">
                  <strong>{intl.standalone.HEADER}</strong>
                </div>
                <Link
                  href={BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year })}
                  passHref
                  className="underline text-primary"
                >
                  {intl.standalone.LINK_TEXT}
                </Link>
              </div>

              <div className="grid grid-cols-1 gap-4 my-4 lg:gap-6 md:grid-cols-2 lg:grid-cols-3">
                {selectedStandaloneWithProductsByCategory.map((bp) => (
                  <StandaloneCardSimple
                    key={bp.variationId}
                    id={bp.productId + bp.variationId}
                    Image={() => (
                      <Image
                        alt={bp?.variation?.name || "Produkt"}
                        src={
                          bp.variation
                            ? getImageFromVariation({
                                variation: bp.variation,
                              })
                            : BASE_64_1X1_GREY
                        }
                        fill
                        className="object-cover rounded-l"
                        sizes="(min-width: 768px) 10vw, 35vw"
                      />
                    )}
                    quantity={bp.quantity}
                    name={bp?.variation?.name}
                    hideAmount
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="row-start-1 mb-4 lg:mb-0 lg:row-start-3 lg:row-span-3 col-span-full lg:col-span-3 xl:col-span-3">
          <Card className="pt-6">
            <h3 className="text-xl">
              <strong className="lg:hidden">{intl.receipt.HEADER}</strong>
              <strong className="hidden lg:block">
                {intl.HAVE_A_NICE_MEAL}
              </strong>
            </h3>

            <div className="mt-4 mb-3">
              <strong>{intl.receipt.SUB_HEADER}</strong>
            </div>

            <ShoppingCart deviation={deviationBasketForCurrentWeekYear} />
          </Card>

          <div className="pl-2 mt-4 text-xs">
            {intl.receipt.ACCEPT_TERMS_LINK_PREFIX}{" "}
            <Link
              href={BRAND_PATHS.TOS_PATH}
              passHref
              className="underline text-primary"
              target="_blank"
            >
              {intl.receipt.ACCEPT_TERMS_LINK_TEXT}
            </Link>
          </div>
        </div>
      </AppShell.Main>

      <FloatingActionBar>
        <div
          className="col-span-1 col-start-1 md:col-start-9 md:text-right"
          id="total-price"
        >
          <div>{intl.SUM}</div>
          <div>
            <Currency>{totalSum}</Currency>
          </div>
        </div>
        <Button
          onClick={() => {
            onSubmitWeek(deviationProductsForCurrentWeekYear);
          }}
          disabled={!hasSingleProduct && isBelowMinMeals}
          loading={isConfirmLoading}
          primary
          className="col-span-3 col-start-10 no-underline"
          id="confirm-order-button"
        >
          {isAccountPaused(billingData.billing.status)
            ? intl.RESTART_DELIVERY_BUTTON
            : intl.ACCEPT_BUTTON}
        </Button>
      </FloatingActionBar>
    </AppShell>
  );
};
